import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { notifications as notifApi } from "@/lib/api";
import { Bell, CheckCircle2, XCircle, Clock, Send, CheckCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    notifApi.list()
      .then(({ data }) => setItems(data || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const markAll = async () => {
    await notifApi.markAllRead();
    load();
  };

  const open = async (n) => {
    if (!n.read) await notifApi.markRead(n.notification_id);
    nav(`/quotes/${n.quote_id}`);
  };

  const iconFor = (t) =>
    t === "quote_accepted" ? { I: CheckCircle2, cls: "bg-green-50 text-[#10B981]" } :
    t === "quote_declined" ? { I: XCircle, cls: "bg-red-50 text-[#EF4444]" } :
    t === "quote_modification_requested" ? { I: Clock, cls: "bg-amber-50 text-[#EA580C]" } :
    { I: Send, cls: "bg-blue-50 text-[#2563EB]" };

  return (
    <div className="p-5 sm:p-8 max-w-3xl mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2563EB]">Activity</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight text-[#001F3F]"
              style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
            Notifications
          </h1>
        </div>
        {items.some((n) => !n.read) && (
          <Button onClick={markAll} variant="outline"
                  className="rounded-full border-slate-300 h-10 text-[#001F3F]">
            <CheckCheck size={14} className="mr-2" /> Mark all as read
          </Button>
        )}
      </div>

      <div className="mt-6 rounded-lg border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#2563EB]" /></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <Bell className="mx-auto text-[#64748B]" size={28} />
            <div className="mt-3 text-sm text-[#64748B]">No notifications yet. When customers respond, they&apos;ll show up here.</div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((n) => {
              const { I: Icon, cls } = iconFor(n.type);
              return (
                <button key={n.notification_id} onClick={() => open(n)}
                        className={`w-full text-left p-4 flex items-start gap-3 hover:bg-slate-50 transition-colors ${
                          !n.read ? "bg-[#F8FAFC]" : ""
                        }`}>
                  <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${cls}`}>
                    <Icon size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#001F3F]">{n.message}</div>
                    <div className="text-xs text-[#64748B] mt-0.5">
                      {n.quote_number} · {n.customer_name}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1 uppercase tracking-[0.1em]">
                      {n.created_at ? format(new Date(n.created_at), "MMM d, yyyy HH:mm") : ""}
                      {n.email_sent && <span className="ml-2 text-[#10B981]">• email sent</span>}
                    </div>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-[#2563EB] mt-1.5" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
